<template>
  <div class="app-navbar">
    <nav ref="navRef" :class="{ expanded }" :style="{ color: navColor }">
      <ul class="nav-root">
        <li class="nav-group logo">
          <div class="nav-item">
            <router-link class="nav-link" to="/">
              <SvgIcon
                v-if="mobile"
                class="logo-toothy"
                :src="logoToothy"
                height="30"
                :solid="false"
              />
              <SvgIcon class="logo-text" :src="logoText" width="200" />
            </router-link>
          </div>
        </li>

        <li v-if="!mobile" class="nav-group main">
          <div v-for="(app, key) in apps" :key="key" class="nav-item">
            <a class="nav-link" :href="app.website" target="_blank">
              <span class="route-title">{{ app.name }}</span>
            </a>
          </div>

          <div v-for="page in sitePageItems" :key="page.uid" class="nav-item">
            <router-link
              class="nav-link"
              :to="{ name: page.route }"
              active-class="current"
            >
              <span class="route-title">{{ page.label }}</span>
              <span class="route-underline" />
            </router-link>
          </div>
        </li>

        <li v-if="!mobile" class="nav-group subscribe">
          <div class="nav-item nav-btn">
            <button @click="store.setNewsLetterPopupState(true)">Subscribe</button>
          </div>
        </li>

        <li v-if="mobile" class="nav-group mobile">
          <AppMenuMobileButton
            v-if="!expanded"
            :open="false"
            @click="toggleMenu(!expanded)"
          />

          <AppMenuMobile v-if="expanded" @close="expanded = false" />
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { nanoid } from 'nanoid';
import { useAppStore } from '@/stores/app';
import SvgIcon from '@/components/SvgIcon.vue';
import AppMenuMobileButton from '@/components/AppMenuMobileButton.vue';
import AppMenuMobile from '@/components/AppMenuMobile.vue';

import logoTextSvg from '@/assets/images/logo_text.svg?raw';
import logoToothySvg from '@/assets/images/logo_toothy.svg?raw';

const store = useAppStore();
const route = useRoute();

const expanded = ref(false);
const navRef = ref<HTMLElement | null>(null);

const mobile = computed(() => store.mobile);
const navPages = computed(() => store.navPages);
const apps = computed(() => store.apps);

const navColor = computed(() => (route.meta?.navColor as string) || 'gray');
const logoText = computed(() => logoTextSvg);
const logoToothy = computed(() => logoToothySvg);

const sitePageItems = computed(() =>
  navPages.value.map((item) => ({ uid: nanoid(5), ...item }))
);

function toggleMenu(active = false) {
  expanded.value = active;
}
</script>

<style scoped lang="scss">
.logo-toothy {
  margin-right: 20px;
}

nav {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 6;
  color: white;
  max-width: 1200px;
  margin: 0 auto;
}

.nav-root {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
}

.nav-group {
  display: inline-flex;
  align-items: center;

  &.logo {
    padding-left: 20px;
  }

  &.main {
    flex-grow: 1;
    justify-content: center;

    .nav-link {
      padding: 0 20px;
    }

    @include tablet {
      .nav-link {
        padding: 0 10px;
      }
    }
  }

  &.subscribe {
    padding-right: 20px;
  }

  &.mobile {
    padding-right: 20px;
  }
}

.nav-item {
  height: $navbar-height;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.nav-link {
  display: flex;
  justify-content: center;
  align-items: center;
  color: inherit;
  font-weight: 400;
  height: 100%;
  width: 100%;
  text-decoration: none;
  opacity: 1;
  transition: 0.25s;

  &:hover {
    opacity: 0.6;
  }

  &.current {
    opacity: 1;
  }

  .nav-group.main & {
    flex-direction: column;
  }
}

.nav-btn > button {
  padding: 0.5em 1.1em;
  display: flex;
  justify-content: center;
  align-items: center;
  color: inherit;
  width: 100%;
  opacity: 1;
  position: relative;
  font-weight: bold;
  transform: scale(1);
  transition: 0.25s;
  z-index: 3;
  background: transparent;
  border: 2px solid currentColor;
  border-radius: 2em;
  line-height: 1em;

  &:hover {
    background-color: #ffffff61;
  }
}

.route-title {
  position: relative;
  font-weight: bold;
  transform: scale(1);
  transition: 0.25s;
  z-index: 3;

  .nav-item > a.current & {
    margin-top: -10px;
    margin-bottom: 4px;
  }
}

.route-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
  transition: 0.25s;

  .nav-item > a:hover & {
    opacity: 0.6;
  }
}

.route-underline {
  height: 2px;
  background: white;
  width: 0;
  margin-top: -2px;
  border-radius: 3px;
  opacity: 0;
  transition:
    opacity 0.2s,
    width 0.4s cubic-bezier(0.13, 2, 1, 1);

  .nav-item > a.current & {
    opacity: 1;
    width: 100%;
  }
}

.menu-button {
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 11.5px 8px;
  margin: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 1;

  > div {
    position: relative;
    width: 100%;
    height: 100%;
  }
}
</style>
